import { Component, OnInit } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { IzzyChannelTypeActionEntity, IzzyIzzyActionEntity, IzzyMetadataEngine } from 'mj_generatedentities';
import { BCSaaSChannelTypeEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';

interface ActionDisplay {
  action: IzzyChannelTypeActionEntity;
  izzyAction: IzzyIzzyActionEntity | null;
  isEditing: boolean;
  editForm: {
    status: string;
    sequence: number;
    configuration: string;
    credentialId: string | null;
  };
}

// Registration function for loader
export function LoadChannelTypeActionsComponent() {}

@Component({
  standalone: false,
  selector: 'izzy-channel-type-actions',
  templateUrl: './channel-type-actions.component.html',
  styleUrls: ['./channel-type-actions.component.css']
})
export class ChannelTypeActionsComponent implements OnInit {
  // Data
  organizations: mjBizAppsCommonOrganizationEntity[] = [];
  channelTypes: BCSaaSChannelTypeEntity[] = [];
  filteredChannelTypes: BCSaaSChannelTypeEntity[] = [];
  channelTypeActions: ActionDisplay[] = [];
  availableActions: IzzyIzzyActionEntity[] = [];

  // Selection
  selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  selectedChannelType: BCSaaSChannelTypeEntity | null = null;

  // UI State
  isLoading = false;
  isSaving = false;
  isCreating = false;
  errorMessage = '';
  successMessage = '';
  statusFilter = 'All';
  statusOptions = ['All', 'Active', 'Pending', 'Revoked'];

  // Create form
  createForm = {
    actionId: '',
    status: 'Active',
    sequence: 0,
    configuration: '',
    credentialId: null as string | null
  };

  // Confirm dialog
  confirmDialogVisible = false;
  confirmDialogTitle = '';
  confirmDialogMessage = '';
  confirmDialogAction: (() => void) | null = null;

  private md: Metadata;

  constructor() {
    this.md = new Metadata();
  }

  async ngOnInit(): Promise<void> {
    await this.loadInitialData();
  }

  async loadInitialData(): Promise<void> {
    this.isLoading = true;
    try {
      // Load organizations
      const orgRv = new RunView();
      const orgResult = await orgRv.RunView({
        EntityName: 'MJ_BizApps_Common: Organizations',
        OrderBy: 'Name'
      });
      if (orgResult.Success) {
        this.organizations = orgResult.Results as mjBizAppsCommonOrganizationEntity[];
        if (this.organizations.length > 0) {
          this.selectedOrganization = this.organizations[0];
        }
      }

      // Load channel types
      const ctRv = new RunView();
      const ctResult = await ctRv.RunView({
        EntityName: 'BC: Channel Types',
        OrderBy: 'Name'
      });
      if (ctResult.Success) {
        this.channelTypes = ctResult.Results as BCSaaSChannelTypeEntity[];
        this.filterChannelTypes();
      }

      // Load available actions from IzzyMetadataEngine
      const engine = IzzyMetadataEngine.Instance;
      await engine.Config(false);
      this.availableActions = engine.IzzyActions.filter(a => a.Status === 'Active');

    } catch (error) {
      this.errorMessage = 'Failed to load data: ' + (error as Error).message;
    } finally {
      this.isLoading = false;
    }
  }

  filterChannelTypes(): void {
    if (!this.selectedOrganization) {
      this.filteredChannelTypes = [];
      return;
    }
    // Channel types are global, but filter by org if needed
    this.filteredChannelTypes = this.channelTypes;
    if (this.filteredChannelTypes.length > 0 && !this.selectedChannelType) {
      this.selectedChannelType = this.filteredChannelTypes[0];
      this.loadChannelTypeActions();
    }
  }

  async onOrganizationChange(orgId: string): Promise<void> {
    this.selectedOrganization = this.organizations.find(o => o.ID === orgId) || null;
    this.filterChannelTypes();
    if (this.selectedChannelType) {
      await this.loadChannelTypeActions();
    }
  }

  async onChannelTypeChange(channelTypeId: string): Promise<void> {
    this.selectedChannelType = this.channelTypes.find(ct => ct.ID === channelTypeId) || null;
    if (this.selectedChannelType) {
      await this.loadChannelTypeActions();
    }
  }

  async loadChannelTypeActions(): Promise<void> {
    if (!this.selectedChannelType || !this.selectedOrganization) return;

    this.isLoading = true;
    this.channelTypeActions = [];
    try {
      const rv = new RunView();
      const result = await rv.RunView({
        EntityName: 'Channel Type Actions',
        ExtraFilter: `ChannelTypeID = '${this.selectedChannelType.ID}' AND OrganizationID = '${this.selectedOrganization.ID}'`,
        OrderBy: 'Sequence'
      });

      if (result.Success) {
        const actions = result.Results as IzzyChannelTypeActionEntity[];
        this.channelTypeActions = actions.map(action => ({
          action,
          izzyAction: this.availableActions.find(ia => ia.ActionID === action.ActionID) || null,
          isEditing: false,
          editForm: this.createEditForm(action)
        }));
      }
    } catch (error) {
      this.errorMessage = 'Failed to load channel type actions: ' + (error as Error).message;
    } finally {
      this.isLoading = false;
    }
  }

  createEditForm(action: IzzyChannelTypeActionEntity): ActionDisplay['editForm'] {
    return {
      status: action.Status,
      sequence: action.Sequence,
      configuration: action.Configuration || '',
      credentialId: action.CredentialID || null
    };
  }

  get filteredActions(): ActionDisplay[] {
    if (this.statusFilter === 'All') {
      return this.channelTypeActions;
    }
    return this.channelTypeActions.filter(a => a.action.Status === this.statusFilter);
  }

  getActionName(actionId: string): string {
    const action = this.availableActions.find(a => a.ID === actionId);
    return action?.Name || 'Unknown Action';
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Revoked': return 'status-revoked';
      default: return '';
    }
  }

  // CRUD Operations
  startCreate(): void {
    this.isCreating = true;
    this.createForm = {
      actionId: '',
      status: 'Active',
      sequence: this.channelTypeActions.length,
      configuration: '',
      credentialId: null
    };
  }

  cancelCreate(): void {
    this.isCreating = false;
  }

  async saveCreate(): Promise<void> {
    if (!this.selectedChannelType || !this.selectedOrganization || !this.createForm.actionId) return;

    this.isSaving = true;
    try {
      const entity = await this.md.GetEntityObject<IzzyChannelTypeActionEntity>('Channel Type Actions');
      entity.NewRecord();
      entity.ChannelTypeID = this.selectedChannelType.ID;
      entity.OrganizationID = this.selectedOrganization.ID;
      entity.ActionID = this.createForm.actionId;
      entity.Status = this.createForm.status as 'Active' | 'Disabled';
      entity.Sequence = this.createForm.sequence;
      entity.Configuration = this.createForm.configuration || null;
      entity.CredentialID = this.createForm.credentialId || null;

      const saved = await entity.Save();
      if (saved) {
        this.successMessage = 'Channel type action created successfully';
        this.isCreating = false;
        await this.loadChannelTypeActions();
        setTimeout(() => this.successMessage = '', 3000);
      } else {
        throw new Error('Failed to save channel type action');
      }
    } catch (error) {
      this.errorMessage = 'Failed to create action: ' + (error as Error).message;
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: ActionDisplay): void {
    display.isEditing = true;
    display.editForm = this.createEditForm(display.action);
  }

  cancelEdit(display: ActionDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: ActionDisplay): Promise<void> {
    this.isSaving = true;
    try {
      const entity = await this.md.GetEntityObject<IzzyChannelTypeActionEntity>('Channel Type Actions');
      await entity.Load(display.action.ID);

      entity.Status = display.editForm.status as 'Active' | 'Disabled';
      entity.Sequence = display.editForm.sequence;
      entity.Configuration = display.editForm.configuration || null;
      entity.CredentialID = display.editForm.credentialId || null;

      const saved = await entity.Save();
      if (saved) {
        this.successMessage = 'Channel type action updated successfully';
        display.isEditing = false;
        await this.loadChannelTypeActions();
        setTimeout(() => this.successMessage = '', 3000);
      } else {
        throw new Error('Failed to save changes');
      }
    } catch (error) {
      this.errorMessage = 'Failed to update action: ' + (error as Error).message;
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(display: ActionDisplay): void {
    this.confirmDialogTitle = 'Delete Channel Type Action';
    this.confirmDialogMessage = `Are you sure you want to delete the action "${this.getActionName(display.action.ActionID)}"? This will affect all channels of this type.`;
    this.confirmDialogAction = () => this.deleteAction(display);
    this.confirmDialogVisible = true;
  }

  closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  confirmDialogYes(): void {
    if (this.confirmDialogAction) {
      this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  async deleteAction(display: ActionDisplay): Promise<void> {
    this.isSaving = true;
    try {
      const entity = await this.md.GetEntityObject<IzzyChannelTypeActionEntity>('Channel Type Actions');
      await entity.Load(display.action.ID);
      const deleted = await entity.Delete();
      if (deleted) {
        this.successMessage = 'Channel type action deleted successfully';
        await this.loadChannelTypeActions();
        setTimeout(() => this.successMessage = '', 3000);
      } else {
        throw new Error('Failed to delete action');
      }
    } catch (error) {
      this.errorMessage = 'Failed to delete action: ' + (error as Error).message;
    } finally {
      this.isSaving = false;
    }
  }

  // Reordering
  async moveUp(display: ActionDisplay): Promise<void> {
    const index = this.filteredActions.indexOf(display);
    if (index <= 0) return;
    await this.swapSequence(display, this.filteredActions[index - 1]);
  }

  async moveDown(display: ActionDisplay): Promise<void> {
    const index = this.filteredActions.indexOf(display);
    if (index >= this.filteredActions.length - 1) return;
    await this.swapSequence(display, this.filteredActions[index + 1]);
  }

  async swapSequence(a: ActionDisplay, b: ActionDisplay): Promise<void> {
    this.isSaving = true;
    try {
      const tempSeq = a.action.Sequence;

      const entityA = await this.md.GetEntityObject<IzzyChannelTypeActionEntity>('Channel Type Actions');
      await entityA.Load(a.action.ID);
      entityA.Sequence = b.action.Sequence;
      await entityA.Save();

      const entityB = await this.md.GetEntityObject<IzzyChannelTypeActionEntity>('Channel Type Actions');
      await entityB.Load(b.action.ID);
      entityB.Sequence = tempSeq;
      await entityB.Save();

      await this.loadChannelTypeActions();
    } catch (error) {
      this.errorMessage = 'Failed to reorder actions: ' + (error as Error).message;
    } finally {
      this.isSaving = false;
    }
  }

  // Utility
  openActionRecord(display: ActionDisplay): void {
    // Navigate to MJ record view
    window.open(`/resource/record/${display.action.ID}/Channel Type Actions`, '_blank');
  }
}
